/* eslint-disable no-console */
import React, { useState, useCallback, useContext } from 'react';
import styled from 'styled-components';
import { Popover } from 'react-tiny-popover';

import {
  generateObjectId,
  tableHelper,
  generateReadableTableColumnId,
} from '@builttocreate/engine-utils';
import {
  faAngleDown,
  faAngleUp,
} from '@fortawesome/free-solid-svg-icons';

import FieldTableColumnTypes from '../../../constants/FieldTableColumnTypes';
import DecoratorIconOptions from '../../../constants/DecoratorIconOptions';
import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';
import Button from '../../misc/Button';
import Icon from '../../misc/Icon';
import PopoverMenu from '../../misc/PopoverMenu';
import DecoratorConfigContext from '../../DecoratorConfigContext';

const {
  getDefaultTextColumn,
  getDefaultNumberColumn,
  getDefaultDropdownColumn,
  getDefaultImageColumn,
  getDefaultMultiSelectColumn,
  getDefaultDateColumn,
  getDefaultBlockColumn,
  getDefaultBarcodeColumn,
  getDefaultSignatureColumn
} = tableHelper;

import { updateSchemaWithReadableIds } from '../../../lib/collectionHelper';

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  overflow: scroll;
  width: 100%;
  height: 100%;



  // Hide scrollbar for Chrome, Safari and Edge
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledTable = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  width: 100%;
`;

const StyledTableTitle = styled.div`
  display: flex;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
`;

const StyledHeaderRow = styled.div`
  display: flex;
  width: fit-content;
  border: 1px solid #ccc;
  background-color: #f5f5f5;
`;

const StyledTHWrapper = styled.div`
  display: flex;
  align-items: stretch;
  gap: 4px;
`;

const StyledTHIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const StyledTH = styled.div`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  justify-content: space-between;
  width: 100%;
  font-weight: 600;
  font-size: 12px;
  border-right: ${(props) => (props.isLast ? 'none' : '1px solid #ccc')};
  padding: 6px 16px;
  vertical-align: top;
  gap: 4px;
`;

const StyledColumnTitleWrapper = styled.div`
  width: 120px;
  ${(props) => props.textOverflow === 'ellipsis'
    ? 'white-space: nowrap; overflow: hidden; text-overflow: ellipsis;'
    : 'overflow-wrap: break-word; white-space: normal;'}
`;

const StyledColumnControl = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const StyledColumnDecoratorWrapper = styled.div`
  max-width: 200px;
  overflow: hidden;
  max-height: 18px;
  display: flex;
  align-items: center;
`;

const NestedTableContainer = styled.div`
  padding-left: ${(props) => props.depth * 24}px;
  width: 100%;
`;

const StyledAddIcon = styled.span`
  &:hover {
    cursor: pointer;
  }
`;

const StyledRowDecoratorsTH = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 4px 8px;
  border: 1px solid #ccc;
  background-color: #f5f5f5;
  white-space: nowrap;
  width: ${(props) => props.$maxWidth || 200}px;
  min-width: ${(props) => props.$maxWidth || 200}px;
  max-width: ${(props) => props.$maxWidth || 200}px;
  overflow: hidden;

  > span {
    border: none;
    padding: 0;
    margin: 0;
    font-size: 12px;

    svg {
      font-size: 12px;
    }
  }
`;

const StyledDecoratorPill = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: ${(props) => (props.$hasLabel ? '3px' : '0')};
  padding: ${(props) => (props.$hasLabel ? '0px 5px' : '3px 5px')};
  margin-left: 4px;
  border-radius: 3px;
  border: 1px solid ${(props) => props.$color};
  font-size: 10px;
  font-weight: 500;
  color: ${(props) => props.$color};
  cursor: default;
  white-space: nowrap;
  line-height: ${(props) => (props.$hasLabel ? '1.4' : '1')};

  svg {
    font-size: 10px;
    flex-shrink: 0;
  }
`;

const StyledDecoratorPillLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const StyledKebabTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: default;
  background: none;
  border: none;
  padding: 1px 2px;
  margin-left: 4px;
  border-radius: 3px;
  opacity: 0.55;

  &:focus {
    outline: none;
  }

  svg {
    font-size: 12px;
  }
`;

const StyledDecoratorMenu = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.$bg};
  border-radius: ${(props) => props.$radius}px;
  border: 1px solid ${(props) => props.$border};
  box-shadow: 0px 4px 20px rgba(20, 20, 23, 0.18);
  min-width: 160px;
  max-width: 240px;
  overflow: hidden;
  font-family: ${(props) => props.$fontFamily};
`;

const StyledDecoratorMenuRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;

  & + & {
    border-top: 1px solid ${(props) => props.$border};
  }
`;

const StyledDecoratorMenuIconWrapper = styled.div`
  flex-shrink: 0;
  width: 14px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 12px;
  }
`;

const StyledDecoratorMenuLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => props.$color};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
`;

const getIconDefinition = (iconValue) => {
  if (!iconValue) return null;
  const match = DecoratorIconOptions.find((opt) => opt.value === iconValue);
  return match ? match.icon : ThemeIcons.decorator.image;
};

const StyledEditorDecoratorScroll = styled.div`
  display: flex;
  align-items: center;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

/**
 * Renders decorators in the Structure editor view.
 * Row decorators (`decoratorScope="row"`) use visibleLimitInRows; column decorators
 * (`decoratorScope="column"`) use visibleLimitInFields — matching TableFieldHeader /
 * TableEditorHeader. Above the inline limit, decorators collapse into a kebab menu.
 */
const EditorDecorators = ({ decorators, primaryColor, theme, decoratorScope = 'row' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { visibleLimitInRows, visibleLimitInFields } = useContext(DecoratorConfigContext);

  const visibleInlineLimit = decoratorScope === 'column' ? visibleLimitInFields : visibleLimitInRows;

  if (!decorators || !Array.isArray(decorators) || decorators.length === 0) return null;

  const validDecorators = decorators.filter((d) => d.icon || d.label);
  if (validDecorators.length === 0) return null;

  if (validDecorators.length > visibleInlineLimit) {
    const menuContent = (
      <StyledDecoratorMenu
        $bg={theme.backgroundColor || '#fff'}
        $radius={theme.borderRadius || 4}
        $border={theme.borderColor || '#ccc'}
        $fontFamily={theme.fontFamily}
      >
        {validDecorators.map((decorator, index) => {
          const iconDef = getIconDefinition(decorator.icon);
          const color = decorator.color || primaryColor;
          return (
            <StyledDecoratorMenuRow
              key={decorator._id || index}
              $border={theme.borderColor || '#ccc'}
            >
              {iconDef ? (
                <StyledDecoratorMenuIconWrapper>
                  <Icon icon={iconDef} color={color} />
                </StyledDecoratorMenuIconWrapper>
              ) : null}
              {decorator.label ? (
                <StyledDecoratorMenuLabel $color={color}>{decorator.label}</StyledDecoratorMenuLabel>
              ) : null}
            </StyledDecoratorMenuRow>
          );
        })}
      </StyledDecoratorMenu>
    );

    return (
      <StyledEditorDecoratorScroll>
        <Popover
          isOpen={isOpen}
          positions={['bottom', 'top', 'right', 'left']}
          reposition={true}
          containerStyle={{ zIndex: '2147483647' }}
          onClickOutside={() => setIsOpen(false)}
          content={menuContent}
        >
          <StyledKebabTrigger
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen((prev) => !prev);
            }}
          >
            <Icon icon={ThemeIcons.misc.decorators} />
          </StyledKebabTrigger>
        </Popover>
      </StyledEditorDecoratorScroll>
    );
  }

  return (
    <StyledEditorDecoratorScroll>
      {validDecorators.map((decorator, index) => {
        const iconDef = getIconDefinition(decorator.icon);
        const color = decorator.color || primaryColor;
        return (
          <StyledDecoratorPill key={decorator._id || index} $color={color} $hasLabel={!!decorator.label}>
            {iconDef ? <Icon icon={iconDef} color={color} /> : null}
            {decorator.label ? <StyledDecoratorPillLabel>{decorator.label}</StyledDecoratorPillLabel> : null}
          </StyledDecoratorPill>
        );
      })}
    </StyledEditorDecoratorScroll>
  );
};

// Component to render a single table with potential child tables
const SchemaComponent = React.memo(({
  schema,
  fieldPositionSchema,
  decoratorColumnWidth,
  onAddColumn,
  onMoveColumn,
  onAddChildSchema,
  onMoveChildSchema,
  onOpenColumnSettings,
  onOpenSchemaSettings,
  columnSettingsConfig,
  columnTitleTextOverflow,
}) => {

  const theme = useContext(ThemeContext);

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const items = [
    {
      text: 'Settings',
      icon: { icon: ThemeIcons.settings.settings, size: 'sm' },
      onClick: () => {
        setIsPopoverOpen(false);
        onOpenSchemaSettings(schema.id);
      }
    },
    {
      text: 'Move Up',
      icon: { icon: faAngleUp, size: 'sm' },
      onClick: () => {
        onMoveChildSchema(schema.parentId, schema.id, 'left');
        setIsPopoverOpen(false);
      }
    },
    {
      text: 'Move Down',
      icon: { icon: faAngleDown, size: 'sm' },
      onClick: () => {
        onMoveChildSchema(schema.parentId, schema.id, 'right');
        setIsPopoverOpen(false);
      }
    },
    {
      text: 'Text Column',
      type: 'text',
      icon: { icon: ThemeIcons.fields.text, size: 'sm' },
      onClick: () => {
        onAddColumn(schema.id, FieldTableColumnTypes.text);
        setIsPopoverOpen(false);
      }
    },
    {
      text: 'Dropdown Column',
      type: 'dropdown',
      icon: { icon: ThemeIcons.fields.dropdown, size: 'sm' },
      onClick: () => {
        onAddColumn(schema.id, FieldTableColumnTypes.dropdown);
        setIsPopoverOpen(false);
      }
    },
    {
      text: 'Selection Column',
      type: 'multiSelect',
      icon: { icon: ThemeIcons.fields.multiSelect, size: 'sm' },
      onClick: () => {
        onAddColumn(schema.id, FieldTableColumnTypes.multiSelect);
        setIsPopoverOpen(false);
      }
    },
    {
      text: 'Image Column',
      type: 'image',
      icon: { icon: ThemeIcons.fields.image, size: 'sm' },
      onClick: () => {
        onAddColumn(schema.id, FieldTableColumnTypes.image);
        setIsPopoverOpen(false);
      }
    },
    {
      text: 'Number Column',
      type: 'number',
      icon: { icon: ThemeIcons.fields.number, size: 'sm' },
      onClick: () => {
        onAddColumn(schema.id, FieldTableColumnTypes.number);
        setIsPopoverOpen(false);
      }
    },
    {
      text: 'Date Time Column',
      type: 'date',
      icon: { icon: ThemeIcons.fields.date, size: 'sm' },
      onClick: () => {
        onAddColumn(schema.id, FieldTableColumnTypes.date);
        setIsPopoverOpen(false);
      }
    },
    {
      text: 'Label Column',
      type: 'block',
      icon: { icon: ThemeIcons.fields.blockParagraph, size: 'sm' },
      onClick: () => {
        onAddColumn(schema.id, FieldTableColumnTypes.block);
        setIsPopoverOpen(false);
      }
    },
    {
      text: 'Barcode Column',
      type: 'barcode',
      icon: { icon: ThemeIcons.fields.barcode, size: 'sm' },
      onClick: () => {
        onAddColumn(schema.id, FieldTableColumnTypes.barcode);
        setIsPopoverOpen(false);
      }
    },
    {
      text: 'Signature Column',
      type: 'signature',
      icon: { icon: ThemeIcons.fields.signature, size: 'sm' },
      onClick: () => {
        onAddColumn(schema.id, FieldTableColumnTypes.signature);
        setIsPopoverOpen(false);
      }
    },
    {
      text: 'Nested Table',
      icon: { icon: ThemeIcons.fields.table, size: 'sm' },
      onClick: () => {
        onAddChildSchema(schema.id);
        setIsPopoverOpen(false);
      }
    },
  ];

  const { types } = columnSettingsConfig || [];

  const filteredItems = (types && types.length > 0 ? items.filter(
    (item) => !item.type || types.includes(item.type)
  ) : items.filter(
    (item) => !item.type || item.type === 'text' || item.type === 'dropdown' || item.type === 'image'
  )).filter(
    (item) => {
      if (schema.root && (item.text === 'Move Up' || item.text === 'Move Down')) {
        return false;
      }
      return true;
    }
  );

  return (
    <NestedTableContainer depth={schema.depth}>
      <StyledTable>
        <StyledTableTitle>
          {!schema.hidden ? null : (
            <>
              <Icon icon={ThemeIcons.settings.hide} color={theme.warningColor} />
              {' '}
&nbsp;
            </>
          )}
          {schema.title}
        </StyledTableTitle>
        <StyledTHWrapper>
          <PopoverMenu
            reposition={false}
            isOpen={isPopoverOpen}
            positions={['right', 'bottom']}
            padding={18}
            onClickOutside={() => setIsPopoverOpen(false)}
            items={filteredItems}
          >
            <StyledAddIcon>
              <Button
                icon={ThemeIcons.misc.add}
                onClick={(e) => {
                  if (e) e.stopPropagation();
                  setIsPopoverOpen(true);
                }}
              />
            </StyledAddIcon>
          </PopoverMenu>
          {schema.rowDecorators && schema.rowDecorators.filter((d) => !d.deleted && (d.icon || d.label)).length > 0 ? (
            <StyledRowDecoratorsTH $maxWidth={decoratorColumnWidth}>
              <EditorDecorators
                decorators={schema.rowDecorators.filter((d) => !d.deleted)}
                primaryColor={theme.primaryColor}
                theme={theme}
              />
            </StyledRowDecoratorsTH>
          ) : null}
          <StyledHeaderRow>
            {schema.tableColumns.map((column, index) => (
              <StyledTH
                key={column._id}
                isLast={index === schema.tableColumns.length - 1}
              >
                {(column.hidden || column.hiddenViews?.length > 0 || column.logic?.conditions?.length > 0) ? (
                  <Icon icon={ThemeIcons.settings.hide} color={theme.warningColor} size="sm" />
                ) : null}
                <StyledColumnTitleWrapper textOverflow={columnTitleTextOverflow}>
                  {column.title}
                </StyledColumnTitleWrapper>
                <StyledColumnDecoratorWrapper>
                  <EditorDecorators
                    decoratorScope="column"
                    decorators={column.decorators}
                    primaryColor={theme.primaryColor}
                    theme={theme}
                  />
                </StyledColumnDecoratorWrapper>
                <StyledColumnControl>
                  {index > 0 && (
                    <StyledTHIcon
                      title="Move left"
                      onClick={() => onMoveColumn(schema.id, column._id, 'left')}
                    >
                      <Icon icon={ThemeIcons.tableSettings.columnMoveLeft} />
                    </StyledTHIcon>
                  )}
                  {index < schema.tableColumns.length - 1 && (
                    <StyledTHIcon
                      title="Move left"
                      onClick={() => onMoveColumn(schema.id, column._id, 'right')}
                    >
                      <Icon icon={ThemeIcons.tableSettings.columnMoveRight} />
                    </StyledTHIcon>
                  )}
                  <StyledTHIcon
                    title="Column settings"
                    onClick={() => onOpenColumnSettings(column._id, schema.id)}
                  >
                    <Icon icon={ThemeIcons.settings.settings} />
                  </StyledTHIcon>
                </StyledColumnControl>
              </StyledTH>
            ))}
          </StyledHeaderRow>
        </StyledTHWrapper>
      </StyledTable>
    </NestedTableContainer>
  );
});

const CollectionEditor = (props) => {

  const { schema, fieldPositionSchema, rowDecorators, decoratorColumnWidth, onChange, onColumnEdit, onSchemaEdit, fieldSettings, fieldId, features, columnTitleTextOverflow } = props;

  const fieldSettingsConfig = fieldSettings?.field;
  const columnSettingsConfig = fieldSettingsConfig?.tableColumns;

  /**
   * START Schema Update Methods
   */
  const handleAddColumn = useCallback((schemaId, type) => {

    let newColumn = null;

    //Step 1: Get column by type
    if (type === FieldTableColumnTypes.text) newColumn = getDefaultTextColumn({ width: 0 });
    else if (type === FieldTableColumnTypes.block) newColumn = getDefaultBlockColumn({ width: 0, title: 'Label Column' });
    else if (type === FieldTableColumnTypes.dropdown) newColumn = getDefaultDropdownColumn({ width: 0 });
    else if (type === FieldTableColumnTypes.multiSelect) newColumn = getDefaultMultiSelectColumn({ width: 0, multi: false });
    else if (type === FieldTableColumnTypes.image) {
      newColumn = getDefaultImageColumn({ width: 0 });
      newColumn.maxImageWidth = 200;
      newColumn.maxImageHeight = 200;
    }
    else if (type === FieldTableColumnTypes.number) newColumn = getDefaultNumberColumn({ width: 0 });
    else if (type === FieldTableColumnTypes.date) newColumn = getDefaultDateColumn({ width: 0 });
    else if (type === FieldTableColumnTypes.barcode) newColumn = getDefaultBarcodeColumn({ width: 0 });
    else if (type === FieldTableColumnTypes.signature) newColumn = getDefaultSignatureColumn({ width: 0 });

    //Step 2: Add column to target schema
    const nextSchema = addTableColumnToSchemaImmutable(schema, schemaId, newColumn, fieldId, features?.readableIds);
    if (onChange) onChange(nextSchema);

  }, [schema]);

  const handleMoveColumn = useCallback((schemaId, columnId, direction) => {
    const nextSchema = moveTableColumnInSchemaImmutable(schema, schemaId, columnId, direction);
    if (onChange) onChange(nextSchema);
  }, [schema]);

  const handleAddChildSchema = useCallback((schemaId) => {

    const tableColumns = [getDefaultTextColumn({ width: 0 })];

    //Set the readable ID to the first column is readable IDs are enabled
    tableColumns[0]._id = features?.readableIds ? 
    generateReadableTableColumnId(schemaId, tableColumns, tableColumns[0]._id) : tableColumns[0]._id;

    const newChildSchema = { title: 'New Table', children: [], hidden: false, tableColumns: tableColumns };
  
    const nextSchema = addChildSchemaImmutable(schema, schemaId, generateObjectId(), newChildSchema);

    if (onChange) onChange(nextSchema);
  
  
  }, [schema]);

  const handleMoveChildSchema = useCallback((parentId, childId, direction) => {
    const nextSchema = moveChildSchemaImmutable(schema, parentId, childId, direction);
    if (onChange) onChange(nextSchema);
  }, [schema]);

  /**
   * Generate schema nodes
   */
  const schemas = flattenSchemaTree(schema).map((s) => {
    if (s.root) {
      return { ...s, rowDecorators: s.rowDecorators || rowDecorators };
    }
    return s;
  });

  const schemaNodes = schemas.map((schema) => {
    return (
      <SchemaComponent
        schema={schema}
        fieldPositionSchema={fieldPositionSchema?.[schema.id] || {}}
        decoratorColumnWidth={decoratorColumnWidth}
        onAddColumn={handleAddColumn}
        onMoveColumn={handleMoveColumn}
        onAddChildSchema={handleAddChildSchema}
        onMoveChildSchema={handleMoveChildSchema}
        onOpenColumnSettings={onColumnEdit}
        onOpenSchemaSettings={onSchemaEdit}
        columnSettingsConfig={columnSettingsConfig}
        columnTitleTextOverflow={columnTitleTextOverflow}
      />
    );
  });

  return (
    <StyledWrapper>
      {schemaNodes}
    </StyledWrapper>
  );

};

/**
 * Adds a new table column to the specified schema immutably.
 * Returns a new schema object with the update.
 *
 * @param {Object} schemaObj - The complete schemas object.
 * @param {string} schemaId - The ID of the schema to update.
 * @param {Object} newColumn - The new table column definition to add.
 * @returns {Object} A new schema object with the updated tableColumns.
 */
function addTableColumnToSchemaImmutable(schemaObj, schemaId, newColumn, fieldId, useReadableIds) {

  // Check if the schema exists.
  if (!schemaObj[schemaId]) {
    throw new Error(`Schema with id "${schemaId}" does not exist.`);
  }

  const targetSchema = schemaObj[schemaId];

  // Create a new tableColumns array (or initialize it if not present).
  const newTableColumns = Array.isArray(targetSchema.tableColumns)
    ? [...targetSchema.tableColumns]
    : [];

  const nextSchemaTableColumns = [...newTableColumns]
  nextSchemaTableColumns.push(newColumn);

  //Generate a readable ID for the new column
  const readableColumnId = useReadableIds ? 
    generateReadableTableColumnId (fieldId, nextSchemaTableColumns, newColumn._id) 
    : newColumn._id;

  newColumn._id = readableColumnId;
  newColumn.identifier = `${fieldId}_column_${readableColumnId}`;

  newTableColumns.push(newColumn);

  // Create a new target schema with updated tableColumns.
  const newTargetSchema = {
    ...targetSchema,
    tableColumns: newTableColumns
  };

  // Return a new overall schema object with the updated schema.
  return {
    ...schemaObj,
    [schemaId]: newTargetSchema
  };
}

/**
 * Moves a table column left or right in the specified schema immutably.
 * Returns a new schema object with the updated tableColumns ordering.
 *
 * @param {Object} schemaObj - The complete schemas object.
 * @param {string} schemaId - The ID of the schema to update.
 * @param {string} columnId - The ID of the column to move.
 * @param {string} direction - The direction to move the column ('left' or 'right').
 * @returns {Object} A new schema object with the updated tableColumns.
 * @throws {Error} If the schemaId or columnId is not found, or if direction is invalid.
 */
function moveTableColumnInSchemaImmutable(schemaObj, schemaId, columnId, direction) {
  // Validate that the target schema exists.
  if (!schemaObj[schemaId]) {
    throw new Error(`Schema with id "${schemaId}" does not exist.`);
  }

  const targetSchema = schemaObj[schemaId];
  const tableColumns = Array.isArray(targetSchema.tableColumns) ? targetSchema.tableColumns : [];

  // Find the index of the column to move.
  const currentIndex = tableColumns.findIndex((col) => col._id === columnId);
  if (currentIndex === -1) {
    throw new Error(`Column with id "${columnId}" not found in schema "${schemaId}".`);
  }

  // Determine the new index based on the direction.
  let newIndex;
  if (direction === 'left') {
    newIndex = currentIndex - 1;
  } else if (direction === 'right') {
    newIndex = currentIndex + 1;
  } else {
    throw new Error(`Invalid direction "${direction}". Use "left" or "right".`);
  }

  // If new index is out of bounds, return the original schema object (or you could throw an error).
  if (newIndex < 0 || newIndex >= tableColumns.length) {
    return schemaObj;
  }

  // Create a new tableColumns array with the column moved.
  const newTableColumns = [...tableColumns];
  const [movedColumn] = newTableColumns.splice(currentIndex, 1);
  newTableColumns.splice(newIndex, 0, movedColumn);

  // Create a new target schema with the updated tableColumns.
  const newTargetSchema = {
    ...targetSchema,
    tableColumns: newTableColumns
  };

  // Return a new overall schema object with the updated schema.
  return {
    ...schemaObj,
    [schemaId]: newTargetSchema
  };
}

/**
 * Adds a new child schema ID to the specified schema immutably.
 * Returns a new schema object with the updated children array.
 *
 * @param {Object} schemaObj - The complete schemas object.
 * @param {string} schemaId - The ID of the schema to update.
 * @param {string} newChildSchemaId - The ID of the child schema to add.
 * @param {Object} newChildSchema - The ID of the child schema to add.
 * @returns {Object} A new schema object with the updated children.
 * @throws {Error} If the target schema is not found.
 */
function addChildSchemaImmutable(schemaObj, schemaId, newChildSchemaId, newChildSchema) {

  // Validate that the target schema exists.
  if (!schemaObj[schemaId]) {
    throw new Error(`Schema with id "${schemaId}" does not exist.`);
  }

  const targetSchema = schemaObj[schemaId];

  // Ensure the children property exists and is an array.
  const newChildren = Array.isArray(targetSchema.children)
    ? [...targetSchema.children]
    : [];

  // Add the new child schema ID.
  newChildren.push(newChildSchemaId);



  // Create a new target schema with the updated children.
  const newTargetSchema = {
    ...targetSchema,
    children: newChildren
  };


  let nextSchema = {
     ...schemaObj,
    [newChildSchemaId]: newChildSchema,
    [schemaId]: newTargetSchema
  }

  const flattenedSchemas = flattenSchemaTree(nextSchema);

  const readableIdsForNestedTables = generateReadableIdForNewChildSchema(flattenedSchemas, newChildSchemaId);

  // Update the schema with readable IDs for nested tables
  nextSchema = updateSchemaWithReadableIds(nextSchema, readableIdsForNestedTables);
  
  return nextSchema;

}

/**
 * Moves a child schema ID left or right within the parent's children array immutably.
 * Returns a new schema object with the updated parent's children ordering.
 *
 * @param {Object} schemaObj - The complete schemas object.
 * @param {string} parentSchemaId - The ID of the parent schema.
 * @param {string} childSchemaId - The ID of the child schema to move.
 * @param {string} direction - The direction to move the child ('left' or 'right').
 * @returns {Object} A new schema object with the updated parent's children.
 * @throws {Error} If the parent schema or child schema is not found, or if direction is invalid.
 */
function moveChildSchemaImmutable(schemaObj, parentSchemaId, childSchemaId, direction) {

  // Validate that the parent schema exists.
  if (!schemaObj[parentSchemaId]) {
    throw new Error(`Parent schema with id "${parentSchemaId}" does not exist.`);
  }

  const parentSchema = schemaObj[parentSchemaId];
  // Ensure children exists and is an array.
  const children = Array.isArray(parentSchema.children) ? parentSchema.children : [];

  // Find the index of the child schema to move.
  const currentIndex = children.indexOf(childSchemaId);
  if (currentIndex === -1) {
    throw new Error(`Child schema with id "${childSchemaId}" not found in parent schema "${parentSchemaId}".`);
  }

  // Determine the new index based on the direction.
  let newIndex;
  if (direction === 'left') {
    newIndex = currentIndex - 1;
  } else if (direction === 'right') {
    newIndex = currentIndex + 1;
  } else {
    throw new Error(`Invalid direction "${direction}". Use "left" or "right".`);
  }

  // If new index is out of bounds, return the original schema object.
  if (newIndex < 0 || newIndex >= children.length) {
    return schemaObj;
  }

  // Create a new children array with the child schema moved.
  const newChildren = [...children];
  const [child] = newChildren.splice(currentIndex, 1);
  newChildren.splice(newIndex, 0, child);

  // Create a new parent schema with the updated children.
  const newParentSchema = {
    ...parentSchema,
    children: newChildren
  };

  // Return a new overall schema object with the updated parent schema.
  return {
    ...schemaObj,
    [parentSchemaId]: newParentSchema
  };
}

/**
 *
 * Flattens a nested schema tree into an array of objects and adds a depth property to each object.
 *
 * @param {Object} schemaObj
 * @returns {Array} // An array of objects with the schema's id, depth, and other properties.
 */
function flattenSchemaTree(schemaObj) {

  const flattened = [];

  // Recursive function to traverse the tree.
  function traverse(schemaId, depth, parentId) {
    const node = schemaObj[schemaId];
    if (!node) return;

    // Create a new object that includes the schema's id and its depth.
    flattened.push({ id: schemaId, parentId, depth, ...node });

    // If the node has children, traverse them with increased depth.
    if (node.children && node.children.length > 0) {
      node.children.forEach((childId) => traverse(childId, depth + 1, schemaId));
    }
  }

  // Find and traverse each root node.
  Object.keys(schemaObj).forEach((schemaId) => {
    if (schemaObj[schemaId].root) {
      traverse(schemaId, 0);
    }
  });

  return flattened;
}

/**
 * 
 * @param {*} flattenedSchemas 
 * @returns 
 */
function generateReadableIdForNewChildSchema(flattenedSchemas, newChildSchemaId) {
  
  //Track the count of tables at each depth level
  const depthCountMap = {}; 
  const result = {};

  for (const schema of flattenedSchemas) {
    // Skip the root node
    if (schema.root ) continue; 

    const { id, depth } = schema;

    // Initialize the count for this depth if it doesn't exist
    if (!depthCountMap[depth]) {
      depthCountMap[depth] = 1; 
    } else {
      depthCountMap[depth]++;
    } 

    let tableId = schema.id;

    if(schema.id === newChildSchemaId) {

     tableId = `level${depth}Table${depthCountMap[depth]}`;
      
      while( flattenedSchemas.some((s) => s.id === tableId)) {
        depthCountMap[depth]++;
        tableId = `level${depth}Table${depthCountMap[depth]}`;
      }

    }

    result[id] = tableId;
  }

  return result;

}





export { flattenSchemaTree };
export default CollectionEditor;
